import {
  AttachmentBox_Constraint_,
  AttachmentBox_Update_Column_,
  Esrs_Answer_Constraint_,
  Esrs_Answer_Update_Column_,
  GetEsrsMetricAnswerDocument_,
  GetEsrsMetricsDatapointsDocument_,
  GetSingleEsrsMetricAnswerDocument_,
  EsrsAssessmentAnswersAggregateDocument_,
  useUpsertDatapointMutation,
  DatapointFieldsFragment_,
  EsrsAnswersFieldsFragment_,
  TaskStatusEnum_Enum_,
  useUpdateTaskInProgressMutation,
} from 'models';
import { DatapointSourceEnum, TimePeriodsEnums } from '../../../Requirement';
import { useCallback } from 'react';
import { useAuth } from 'Providers/auth/AuthProvider.tsx';
import { useParams } from 'react-router-dom';
import { NOTAGCOMBINATION } from 'containers/Esrs/utils';

export const usePopulateMetricSummary = ({
  answer,
  datapoint,
  metricRef,
}: {
  answer: EsrsAnswersFieldsFragment_ | undefined;
  datapoint: DatapointFieldsFragment_ | undefined;
  metricRef: string;
}) => {
  const { esrsAssessmentId = '' } = useParams();
  const [upsertDatapoint] = useUpsertDatapointMutation();
  const [updateTaskInProgress] = useUpdateTaskInProgressMutation();
  const { user } = useAuth();
  const isTaskNotStarted =
    datapoint && datapoint?.task?.status === TaskStatusEnum_Enum_.NotStarted_;

  const populateAnswer = useCallback(
    async (generatedAnswer: string, companyReportingUnitId: string) => {
      await upsertDatapoint({
        variables: {
          objects: {
            id: datapoint?.id ?? undefined,
            value: generatedAnswer ?? '',
            factValue: datapoint?.factValue,
            timeframe: TimePeriodsEnums.year,
            tagCombination: NOTAGCOMBINATION,
            authorId: user?.id,
            source: DatapointSourceEnum.AI,
            lastEdited: new Date(),
            answer: {
              data: {
                metricRef: metricRef,
                hasOptedOut: answer?.hasOptedOut ?? false,
                optOutReason: answer?.optOutReason ?? '',
                reportingUnitId: companyReportingUnitId,
                assessmentId: esrsAssessmentId,
                attachmentBox: {
                  data: {},
                  on_conflict: {
                    constraint: AttachmentBox_Constraint_.AttachmentBoxMetricAnswerIdKey1_,
                    update_columns: [AttachmentBox_Update_Column_.MetricAnswerId_],
                  },
                },
                optOutAuthorId: user?.id,
              },
              on_conflict: {
                constraint: Esrs_Answer_Constraint_.AnswerMetricRefAssessmentIdReportingUnitIdKey_,
                update_columns: [
                  Esrs_Answer_Update_Column_.HasOptedOut_,
                  Esrs_Answer_Update_Column_.OptOutReason_,
                  Esrs_Answer_Update_Column_.OptOutAuthorId_,
                ],
              },
            },
          },
        },
        refetchQueries: [
          GetEsrsMetricAnswerDocument_,
          GetEsrsMetricsDatapointsDocument_,
          GetSingleEsrsMetricAnswerDocument_,
          EsrsAssessmentAnswersAggregateDocument_,
        ],
      }).then(() => {
        if (isTaskNotStarted) {
          updateTaskInProgress({
            variables: {
              id: datapoint?.task?.id,
            },
          });
        }
      });
    },
    [upsertDatapoint, datapoint, answer, metricRef, user?.id, esrsAssessmentId]
  );

  return { populateAnswer, datapoint };
};
